import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Loader2, Calendar, Users, Award, Video, BookOpen, Target, Shield, CheckCircle2, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* SKI AXIS Pricing — cinematic dark/gold, synced with landing & UAE Forum */

const PRICING_STYLES = `
.skia-pricing *{box-sizing:border-box;}
.skia-pricing{
  --ink:#FAF7F0;--ink2:#F4EFE6;--ink3:#FFFFFF;--ink4:#FFFFFF;
  --gold:#C8A84B;--gold2:#B5944A;--gold3:#7A6432;
  --gold-line:rgba(200,168,75,0.32);--gold-subtle:rgba(200,168,75,0.08);
  --cream:#0D1B2A;--white:#0D1B2A;--mist:#3A4A5C;--stone:#7A8190;
  background:var(--ink);color:var(--cream);min-height:100vh;
  font-family:'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif;
}
.skia-pricing h1,.skia-pricing h2,.skia-pricing h3{font-family:'Cormorant Display',Georgia,serif;letter-spacing:-.02em;color:var(--white);}

.skia-pricing .p-hero{position:relative;padding:90px 32px 64px;overflow:hidden;background:var(--ink);}
.skia-pricing .p-hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(200,168,75,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(200,168,75,.025) 1px,transparent 1px);background-size:80px 80px;}
.skia-pricing .p-hero-glow{position:absolute;top:-20%;left:-15%;width:65%;height:120%;background:radial-gradient(ellipse at 30% 40%,rgba(200,168,75,.07) 0%,transparent 60%);}
.skia-pricing .p-hero-mono{position:absolute;bottom:-20%;right:-3%;font-family:'Cormorant Display',serif;font-size:clamp(220px,26vw,360px);font-weight:700;font-style:italic;color:rgba(200,168,75,.04);line-height:.85;white-space:nowrap;letter-spacing:-.06em;user-select:none;pointer-events:none;}
.skia-pricing .p-hero-inner{position:relative;z-index:5;max-width:1100px;margin:0 auto;text-align:center;}
.skia-pricing .p-eyebrow{display:inline-flex;align-items:center;gap:10px;font-size:11px;font-weight:700;letter-spacing:.3em;text-transform:uppercase;color:var(--gold);background:var(--gold-subtle);border:1px solid var(--gold-line);padding:8px 18px;border-radius:50px;margin-bottom:28px;}
.skia-pricing .p-eyebrow .dot{width:6px;height:6px;border-radius:50%;background:var(--gold);}
.skia-pricing .p-h1{font-size:clamp(44px,6.4vw,84px);font-weight:700;line-height:.96;margin-bottom:24px;}
.skia-pricing .p-h1 em{font-style:italic;color:var(--gold);}
.skia-pricing .p-sub{font-family:'Cormorant Display',serif;font-size:clamp(18px,2vw,22px);font-weight:300;font-style:italic;color:var(--mist);max-width:720px;margin:0 auto 32px;line-height:1.55;}
.skia-pricing .p-stats{display:flex;flex-wrap:wrap;justify-content:center;gap:14px;margin-top:8px;}
.skia-pricing .p-stat{display:inline-flex;align-items:center;gap:10px;background:var(--ink3);border:1px solid var(--gold-line);padding:12px 20px;border-radius:50px;font-size:12px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:var(--cream);}
.skia-pricing .p-stat svg{color:var(--gold);}

.skia-pricing .p-section{padding:80px 32px;max-width:1320px;margin:0 auto;}
.skia-pricing .p-section-head{text-align:center;margin-bottom:56px;}
.skia-pricing .p-tag{font-size:11px;font-weight:700;letter-spacing:.3em;text-transform:uppercase;color:var(--gold);margin-bottom:14px;display:inline-flex;align-items:center;gap:10px;}
.skia-pricing .p-tag::before,.skia-pricing .p-tag::after{content:'';display:block;width:24px;height:1px;background:var(--gold);opacity:.5;}
.skia-pricing .p-h2{font-size:clamp(36px,5vw,60px);font-weight:700;line-height:1.04;margin-bottom:14px;}
.skia-pricing .p-h2 em{font-style:italic;color:var(--gold);}
.skia-pricing .p-lead{font-family:'Cormorant Display',serif;font-size:18px;font-weight:300;font-style:italic;color:var(--mist);max-width:640px;margin:0 auto;}

.skia-pricing .p-features{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;}
@media (max-width:900px){.skia-pricing .p-features{grid-template-columns:1fr;}}
.skia-pricing .p-feature{background:linear-gradient(170deg,var(--ink3) 0%,var(--ink2) 100%);border:1px solid rgba(13,27,42,.07);padding:32px 28px;position:relative;transition:all .3s;}
.skia-pricing .p-feature:hover{border-color:var(--gold-line);transform:translateY(-3px);}
.skia-pricing .p-feature-icon{width:48px;height:48px;background:var(--gold-subtle);border:1px solid var(--gold-line);border-radius:12px;display:flex;align-items:center;justify-content:center;color:var(--gold);margin-bottom:20px;}
.skia-pricing .p-feature h3{font-family:'DM Sans',sans-serif;font-size:18px;font-weight:700;color:var(--white);margin-bottom:8px;letter-spacing:0;}
.skia-pricing .p-feature p{font-size:14px;color:var(--mist);line-height:1.6;}

.skia-pricing .p-curr{display:flex;flex-direction:column;gap:16px;}
.skia-pricing .p-week{display:grid;grid-template-columns:200px 1fr;background:linear-gradient(170deg,var(--ink3) 0%,var(--ink2) 100%);border:1px solid rgba(13,27,42,.07);overflow:hidden;transition:all .3s;}
@media (max-width:768px){.skia-pricing .p-week{grid-template-columns:1fr;}}
.skia-pricing .p-week:hover{border-color:var(--gold-line);}
.skia-pricing .p-week-badge{background:linear-gradient(135deg,rgba(200,168,75,.12) 0%,rgba(200,168,75,.04) 100%);border-right:1px solid var(--gold-line);padding:32px 24px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;}
@media (max-width:768px){.skia-pricing .p-week-badge{border-right:none;border-bottom:1px solid var(--gold-line);}}
.skia-pricing .p-week-num{font-size:10px;font-weight:700;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);margin-bottom:8px;}
.skia-pricing .p-week-date{font-family:'Cormorant Display',serif;font-size:32px;font-weight:700;color:var(--white);line-height:1;}
.skia-pricing .p-week-yr{font-size:11px;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--mist);margin-top:6px;}
.skia-pricing .p-week-body{padding:32px 36px;}
.skia-pricing .p-week-topic{font-family:'Cormorant Display',serif;font-size:28px;font-weight:700;color:var(--white);margin-bottom:10px;line-height:1.1;}
.skia-pricing .p-week-speaker{font-size:13px;color:var(--gold);margin-bottom:14px;font-weight:600;letter-spacing:.04em;}
.skia-pricing .p-week-speaker span{color:var(--mist);font-weight:400;}
.skia-pricing .p-week-desc{font-size:15px;color:var(--mist);line-height:1.65;margin-bottom:18px;}
.skia-pricing .p-week-takes{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;}
@media (max-width:600px){.skia-pricing .p-week-takes{grid-template-columns:1fr;}}
.skia-pricing .p-take{display:flex;align-items:center;gap:8px;background:var(--gold-subtle);border:1px solid var(--gold-line);padding:8px 12px;border-radius:4px;font-size:12px;color:var(--cream);}
.skia-pricing .p-take svg{color:var(--gold);flex-shrink:0;}

.skia-pricing .p-price-wrap{max-width:680px;margin:0 auto;}
.skia-pricing .p-price{background:linear-gradient(170deg,var(--ink3) 0%,var(--ink2) 100%);border:1px solid var(--gold-line);position:relative;overflow:hidden;}
.skia-pricing .p-price::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(to right,transparent,var(--gold),transparent);}
.skia-pricing .p-price-ribbon{position:absolute;top:24px;right:-44px;background:var(--gold);color:var(--ink);font-size:10px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;padding:6px 56px;transform:rotate(45deg);}
.skia-pricing .p-price-head{padding:48px 40px 32px;text-align:center;border-bottom:1px solid rgba(200,168,75,.1);}
.skia-pricing .p-price-tag{display:inline-block;background:var(--gold);color:var(--ink);font-size:10px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;padding:8px 18px;border-radius:50px;margin-bottom:20px;}
.skia-pricing .p-price-title{font-size:36px;font-weight:700;color:var(--white);margin-bottom:24px;letter-spacing:-.01em;}
.skia-pricing .p-price-row{display:flex;justify-content:center;align-items:flex-end;gap:24px;margin-bottom:20px;flex-wrap:wrap;}
.skia-pricing .p-price-old{text-align:center;}
.skia-pricing .p-price-old .num{font-family:'Cormorant Display',serif;font-size:32px;color:var(--stone);text-decoration:line-through;}
.skia-pricing .p-price-old .lab{font-size:10px;color:var(--stone);letter-spacing:.18em;text-transform:uppercase;margin-top:2px;}
.skia-pricing .p-price-arrow{font-family:'Cormorant Display',serif;font-size:48px;color:var(--gold);line-height:1;}
.skia-pricing .p-price-new{text-align:center;}
.skia-pricing .p-price-new .num{font-family:'Cormorant Display',serif;font-size:88px;font-weight:700;background:linear-gradient(135deg,var(--gold) 0%,var(--gold3) 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;line-height:1;letter-spacing:-.04em;}
.skia-pricing .p-price-new .lab{font-size:11px;color:var(--gold);letter-spacing:.2em;text-transform:uppercase;font-weight:700;margin-top:4px;}
.skia-pricing .p-price-list{padding:32px 40px 40px;}
.skia-pricing .p-price-list ul{list-style:none;padding:0;margin:0 0 28px;display:flex;flex-direction:column;gap:12px;}
.skia-pricing .p-price-list li{display:flex;align-items:flex-start;gap:12px;font-size:15px;color:var(--cream);line-height:1.5;}
.skia-pricing .p-price-list li svg{color:var(--gold);flex-shrink:0;margin-top:2px;}
.skia-pricing .p-price-warn{background:rgba(200,168,75,0.08);border:1px solid var(--gold-line);padding:14px 18px;border-radius:4px;font-size:13px;color:var(--gold);font-weight:600;letter-spacing:.02em;text-align:center;margin-bottom:24px;}
.skia-pricing .p-checkout{width:100%;background:var(--gold);color:var(--ink);font-size:13px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;padding:22px;border:none;border-radius:50px;cursor:pointer;transition:all .25s;display:inline-flex;align-items:center;justify-content:center;gap:10px;}
.skia-pricing .p-checkout:hover:not(:disabled){background:var(--gold2);transform:translateY(-2px);box-shadow:0 12px 36px rgba(200,168,75,.3);}
.skia-pricing .p-checkout:disabled{opacity:.6;cursor:not-allowed;}
.skia-pricing .p-fineprint{text-align:center;font-size:12px;color:var(--stone);margin-top:14px;}

.skia-pricing .p-faq{max-width:780px;margin:0 auto;display:flex;flex-direction:column;gap:12px;}
.skia-pricing .p-faq-item{background:var(--ink3);border:1px solid rgba(13,27,42,.07);padding:24px 28px;transition:all .25s;}
.skia-pricing .p-faq-item:hover{border-color:var(--gold-line);}
.skia-pricing .p-faq-q{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:var(--white);margin-bottom:8px;}
.skia-pricing .p-faq-a{font-size:15px;color:var(--mist);line-height:1.65;}

/* TWO-TIER PLAN CARDS */
.skia-pricing .p-tier-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;max-width:980px;margin:0 auto;}
@media (max-width:820px){.skia-pricing .p-tier-grid{grid-template-columns:1fr;}}
.skia-pricing .p-tier{background:var(--ink3);border:1px solid rgba(13,27,42,.08);padding:48px 36px 36px;position:relative;display:flex;flex-direction:column;}
.skia-pricing .p-tier.featured{background:linear-gradient(180deg,#FFFDF7 0%,#FFFFFF 60%);border:2px solid var(--gold);box-shadow:0 24px 60px -20px rgba(200,168,75,0.35);}
.skia-pricing .p-tier-ribbon{position:absolute;top:0;right:32px;background:var(--gold);color:#07070A;font-size:10px;font-weight:800;letter-spacing:.22em;text-transform:uppercase;padding:8px 16px;}
.skia-pricing .p-tier-head{padding-bottom:24px;border-bottom:1px solid rgba(13,27,42,.08);margin-bottom:24px;}
.skia-pricing .p-tier-name{font-size:11px;font-weight:800;letter-spacing:.28em;text-transform:uppercase;color:var(--gold);margin-bottom:14px;}
.skia-pricing .p-tier-price-row{display:flex;align-items:baseline;gap:4px;margin-bottom:12px;}
.skia-pricing .p-tier-currency{font-family:'Cormorant Display',serif;font-size:32px;font-weight:600;color:var(--white);}
.skia-pricing .p-tier-num{font-family:'Cormorant Display',serif;font-size:88px;font-weight:700;color:var(--white);line-height:1;letter-spacing:-.04em;}
.skia-pricing .p-tier.featured .p-tier-num{background:linear-gradient(135deg,var(--gold) 0%,var(--gold2) 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
.skia-pricing .p-tier-cycle{font-size:13px;font-weight:600;letter-spacing:.04em;color:var(--stone);margin-left:6px;}
.skia-pricing .p-tier-byline{font-family:'Cormorant Display',serif;font-style:italic;font-weight:300;font-size:16px;color:var(--mist);line-height:1.5;}
.skia-pricing .p-tier-list{list-style:none;padding:0;margin:0 0 28px;display:flex;flex-direction:column;gap:12px;flex:1;}
.skia-pricing .p-tier-list li{display:flex;align-items:flex-start;gap:11px;font-size:14.5px;color:var(--cream);line-height:1.55;}
.skia-pricing .p-tier-list li svg{color:var(--gold);flex-shrink:0;margin-top:2px;}
.skia-pricing .p-tier-list li svg.prem{color:var(--gold);filter:drop-shadow(0 0 6px rgba(200,168,75,.4));}
.skia-pricing .p-tier-list li strong{color:var(--white);font-weight:700;}
.skia-pricing .p-tier-cta{width:100%;font-family:inherit;font-size:12px;font-weight:800;letter-spacing:.2em;text-transform:uppercase;padding:18px;border-radius:50px;cursor:pointer;transition:all .25s;display:inline-flex;align-items:center;justify-content:center;gap:8px;border:none;}
.skia-pricing .p-tier-cta.primary{background:var(--gold);color:#07070A;}
.skia-pricing .p-tier-cta.primary:hover:not(:disabled){background:var(--gold2);transform:translateY(-2px);box-shadow:0 14px 40px rgba(200,168,75,.3);}
.skia-pricing .p-tier-cta.primary:disabled{opacity:.6;cursor:not-allowed;}
.skia-pricing .p-tier-cta.secondary{background:transparent;color:var(--cream);border:1.5px solid var(--cream);}
.skia-pricing .p-tier-cta.secondary:hover{background:var(--cream);color:var(--ink3);transform:translateY(-2px);}
.skia-pricing .p-tier-fineprint{text-align:center;font-size:12px;color:var(--stone);margin-top:14px;}
`;

export const PricingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { user, token, isPaid } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'cancelled') {
      toast({
        title: 'Payment Cancelled',
        description: 'Your payment was cancelled. You can try again anytime.',
        variant: 'destructive',
      });
    }
  }, [searchParams]);

  useEffect(() => {
    if (isPaid) {
      toast({ title: 'Already Enrolled!', description: 'You already have access to the program.' });
    }
  }, [isPaid]);

  const handleCheckout = async (tier) => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/create-checkout-session`,
        {
          tier: tier,
          user_id: user?.id || null,
          user_email: user?.email || null,
          success_url: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${window.location.origin}/pricing?payment=cancelled`,
        },
        { headers }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Checkout Error',
        description: error.response?.data?.detail || 'Unable to start checkout. Please try again.',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const programFeatures = [
    { icon: Video, title: 'Live Masterclasses', description: 'Weekly 90-min sessions with Q&A from top operators.' },
    { icon: Users, title: 'Exclusive Cohort', description: 'Network with 20 chosen founders, hand-selected for Cohort 1.' },
    { icon: BookOpen, title: 'Resources Library', description: 'Templates, frameworks, governance playbooks — lifetime.' },
    { icon: Award, title: 'AI Evaluation', description: 'Nova AI institutional governance assessment & scoring.' },
    { icon: Target, title: 'Office Hours', description: 'Direct access to mentors and Ministry-level operators.' },
    { icon: Shield, title: 'Lifetime Access', description: 'Recordings, materials and alumni network forever.' },
  ];

  // Cohort 1: 1 July 2026 → 12 August 2026 (per user requirement: "1ST OF JULY")
  const masterclassSchedule = [
    {
      week: 1,
      date: 'Jul 1',
      topic: 'From Idea to MVP',
      speaker: 'Alex Chen',
      title: 'Founder, $2B Unicorn',
      description: 'Validate your idea, build your first MVP in 30 days, and acquire your first 100 high-signal users.',
      keyTakeaways: ['Product validation frameworks', 'Rapid MVP development', 'Early user acquisition'],
    },
    {
      week: 2,
      date: 'Jul 8',
      topic: 'Fundraising Mastery',
      speaker: 'Sarah Martinez',
      title: 'Partner, Top-Tier VC Fund',
      description: 'Inside secrets of raising seed to Series A. What investors really look for and how to pitch effectively.',
      keyTakeaways: ['Pitch deck essentials', 'Investor psychology', 'Term sheet navigation'],
    },
    {
      week: 3,
      date: 'Jul 15',
      topic: 'Governance for Scale',
      speaker: 'Dr. Ross Rafahël',
      title: 'Founder, SKI Asia Pacific',
      description: 'Build governance frameworks that attract institutional capital and unlock cross-border expansion.',
      keyTakeaways: ['3-Layer Governance Model', 'Compliance roadmap', 'Partnership frameworks'],
    },
    {
      week: 4,
      date: 'Jul 22',
      topic: 'Scaling Across Borders',
      speaker: 'Michael Zhang',
      title: 'Serial Entrepreneur · 3 Exits',
      description: 'Navigate international markets, build global teams, and scale revenue across multiple countries.',
      keyTakeaways: ['Market entry strategies', 'Global team building', 'Cross-border operations'],
    },
    {
      week: 5,
      date: 'Jul 29',
      topic: 'Strategic Partnerships',
      speaker: 'Jennifer Lee',
      title: 'VP Partnerships, Fortune 500',
      description: 'Build partnerships that compound growth — from first contact to signed deals worth millions.',
      keyTakeaways: ['Partnership frameworks', 'Deal negotiation', 'Network effects'],
    },
    {
      week: 6,
      date: 'Aug 5',
      topic: 'Exit Strategies & Beyond',
      speaker: 'David Kumar',
      title: 'Founder · $500M Exit to Google',
      description: 'Plan your exit from day one. IPO vs acquisition strategies and maximising founder outcomes.',
      keyTakeaways: ['Exit planning', 'Valuation optimization', 'Post-exit opportunities'],
    },
  ];

  const faqs = [
    { q: 'Who is Cohort 1 for?', a: '20 chosen MVP-stage founders ready to scale with institutional governance and direct access to UAE / global capital.' },
    { q: 'When does it start?', a: 'Cohort 1 starts 1 July 2026. Applications close 30 June 2026 and are reviewed on a rolling basis (7 working days response).' },
    { q: 'How much is the Cohort 1 programme fee?', a: 'AED 6,900 per founder for the full 12-week governance architecture programme (covers IFZA incorporation consultancy, three-layer governance build, Demo Day, alumni access, partner credits). IFZA government licence and visa fees are payable separately by the founder. Programme payment is flexible — 50% on acceptance, 50% at Week 6.' },
    { q: 'Are there scholarships?', a: 'Yes — the She Built This × SKI Axis scholarship covers 5 fully-funded seats per cohort for women founders. Apply via the standard form and tick the scholarship checkbox in Section 5. Reviewed alongside standard applications.' },
    { q: 'What is the difference between the Cohort fee and Premium platform plan?', a: 'Cohort 1 (AED 6,900) is the live 12-week accelerator programme — entity build, governance architecture, weekly faculty 1:1s, Demo Day with UAE family offices. Premium (AED 1,099/yr) is your year-round platform subscription — Nova AI, masterclass library, document automation, investor pipeline, complimentary access to all 12 monthly webinars. The two are independent: you can apply to Cohort 1 on Basic; Premium fast-tracks your application review.' },
    { q: 'What is included beyond the programme?', a: 'Free UAE incorporation consultancy via IFZA (advisory only — IFZA government licence and visa fees are payable separately by the founder), three-layer governance build, Sentinel assessment, Demo Day at Invest UAE Forum, SKI Axis Alumni Circle, Microsoft / Google / AWS partner credits.' },
    { q: 'What if I miss a session?', a: 'All live sessions are recorded and available in the Alumni Circle (lifetime access).' },
    { q: 'Can I get a refund?', a: 'Platform Premium: 7-day money-back guarantee. Cohort 1 fee: refundable up to 14 days before the cohort start date.' },
  ];

  return (
    <div className="skia-pricing">
      <style dangerouslySetInnerHTML={{ __html: PRICING_STYLES }} />
      <Header />

      {/* Hero */}
      <section className="p-hero">
        <div className="p-hero-grid" />
        <div className="p-hero-glow" />
        <div className="p-hero-mono">PRICING</div>
        <div className="p-hero-inner">
          <div className="p-eyebrow" data-testid="cohort-1-badge">
            <span className="dot" /> Cohort 1 · 1 July → 12 August 2026
          </div>
          <h1 className="p-h1">
            Where 20 chosen founders<br /><em>scale beyond ceilings.</em>
          </h1>
          <p className="p-sub">
            6-week masterclass with world-class founders & investors. Free company registration for the 20 chosen — you cover only visa & licence.
          </p>
          <div className="p-stats">
            <span className="p-stat"><Users size={14} /> 20 chosen founders</span>
            <span className="p-stat"><Calendar size={14} /> Starts 1 July 2026</span>
            <span className="p-stat"><Award size={14} /> Cohort fee · AED 6,900 · 5 scholarships</span>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="p-section">
        <div className="p-section-head">
          <div className="p-tag">What you get</div>
          <h2 className="p-h2">Everything you need to <em>scale</em>.</h2>
          <p className="p-lead">A curated programme. No fluff. Built for MVP-stage founders ready to operate at institutional grade.</p>
        </div>
        <div className="p-features">
          {programFeatures.map((f, i) => (
            <div key={i} className="p-feature" data-testid={`feature-${i}`}>
              <div className="p-feature-icon"><f.icon size={22} /></div>
              <h3>{f.title}</h3>
              <p>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Curriculum */}
      <section className="p-section" style={{ paddingTop: 0 }}>
        <div className="p-section-head">
          <div className="p-tag">6-week curriculum</div>
          <h2 className="p-h2">Learn from those who have <em>built and exited.</em></h2>
          <p className="p-lead">Six live masterclasses. Six operators. Every session ends with founder Q&A.</p>
        </div>
        <div className="p-curr">
          {masterclassSchedule.map((s, i) => (
            <article key={i} className="p-week" data-testid={`week-${s.week}`}>
              <div className="p-week-badge">
                <div className="p-week-num">Week {s.week}</div>
                <div className="p-week-date">{s.date}</div>
                <div className="p-week-yr">2026</div>
              </div>
              <div className="p-week-body">
                <h3 className="p-week-topic">{s.topic}</h3>
                <div className="p-week-speaker">{s.speaker} <span>· {s.title}</span></div>
                <p className="p-week-desc">{s.description}</p>
                <div className="p-week-takes">
                  {s.keyTakeaways.map((t, ti) => (
                    <div key={ti} className="p-take"><CheckCircle2 size={14} /> {t}</div>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Cohort 1 Programme Fee — AED 6,900 + scholarship (Feb 2026) */}
      <section className="p-section" style={{ paddingTop: 0 }}>
        <div className="p-section-head">
          <div className="p-tag">Cohort 1 Accelerator Fee</div>
          <h2 className="p-h2">The programme fee, <em>and how to fund it.</em></h2>
          <p className="p-lead">Cohort 1 itself is a 12-week governance architecture programme — separate from your platform plan above. Here is the fee, what it covers, and how five women founders attend fully funded.</p>
        </div>
        <div className="p-tier-grid" style={{ marginTop: 24 }}>
          {/* COHORT FEE */}
          <article className="p-tier featured" data-testid="tier-cohort-fee">
            <div className="p-tier-ribbon">Cohort 1 · July 2026</div>
            <div className="p-tier-head">
              <div className="p-tier-name">Cohort Fee</div>
              <div className="p-tier-price-row">
                <span className="p-tier-currency">AED</span>
                <span className="p-tier-num">6,900</span>
                <span className="p-tier-cycle">/ founder</span>
              </div>
              <div className="p-tier-byline">12 weeks · three-layer governance architecture · IFZA incorporation consultancy · Demo Day</div>
            </div>
            <ul className="p-tier-list">
              <li><Sparkles size={16} className="prem" /> <strong>Free UAE incorporation consultancy via IFZA</strong> <span style={{ opacity: 0.75, fontWeight: 400 }}>(advisory only · IFZA government licence &amp; visa fees payable separately)</span></li>
              <li><Sparkles size={16} className="prem" /> <strong>Full three-layer governance build</strong> — Structural · Operational · Post-Agreement</li>
              <li><CheckCircle2 size={16} /> AML / KYC framework, IP registration, ESG / SDG alignment</li>
              <li><CheckCircle2 size={16} /> Sentinel governance assessment report (continuous monitoring)</li>
              <li><CheckCircle2 size={16} /> Demo Day at Invest UAE Forum + warm investor intros</li>
              <li><CheckCircle2 size={16} /> SKI Axis Alumni Circle — lifetime access</li>
              <li><CheckCircle2 size={16} /> Microsoft, Google &amp; AWS partner credits</li>
              <li><CheckCircle2 size={16} /> Flexible payment — 50% on acceptance, 50% at Week 6</li>
              <li><Sparkles size={16} className="prem" /> <strong>Bring Your Cohort</strong> — refer a founder, earn <strong>AED 500</strong> off your fee per accepted referral · <a href="/refer" style={{ color: '#C8A84B', textDecoration: 'underline' }}>track refs</a></li>
            </ul>
            <button
              className="p-tier-cta primary"
              onClick={() => navigate('/apply')}
              data-testid="cohort-fee-apply-btn"
            >
              Apply for Cohort 1 →
            </button>
            <div className="p-tier-fineprint">Applications close 30 June 2026 · 7 working days response</div>
          </article>

          {/* SCHOLARSHIP */}
          <article className="p-tier" data-testid="tier-cohort-scholarship">
            <div className="p-tier-head">
              <div className="p-tier-name" style={{ color: '#C8A84B' }}>She Built This × SKI Axis</div>
              <div className="p-tier-price-row">
                <span className="p-tier-currency">AED</span>
                <span className="p-tier-num" style={{ background: 'linear-gradient(135deg,#C8A84B 0%,#B5944A 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>0</span>
                <span className="p-tier-cycle">· fully funded</span>
              </div>
              <div className="p-tier-byline">5 fully-funded seats per cohort for women founders building governance-first ventures.</div>
            </div>
            <ul className="p-tier-list">
              <li><Sparkles size={16} className="prem" /> <strong>Full AED 6,900 fee waived</strong> — 5 seats per cohort</li>
              <li><CheckCircle2 size={16} /> Identical programme — no second-tier curriculum</li>
              <li><CheckCircle2 size={16} /> Free IFZA incorporation consultancy + governance build <span style={{ opacity: 0.75 }}>(advisory · licence &amp; visa fees not included)</span></li>
              <li><CheckCircle2 size={16} /> Demo Day + Alumni Circle (lifetime)</li>
              <li><CheckCircle2 size={16} /> Mentorship from women operators in the SKI Axis network</li>
              <li><CheckCircle2 size={16} /> Apply via the standard form — tick the scholarship checkbox in Section 5</li>
            </ul>
            <button
              className="p-tier-cta secondary"
              onClick={() => navigate('/apply')}
              data-testid="cohort-scholarship-apply-btn"
            >
              Apply with scholarship →
            </button>
            <div className="p-tier-fineprint">Reviewed alongside standard applications · same 7-day timeline</div>
          </article>
        </div>
      </section>

      {/* Pricing — Two-tier */}
      <section className="p-section" style={{ paddingTop: 0 }}>
        <div className="p-section-head">
          <div className="p-tag">Choose your plan</div>
          <h2 className="p-h2">Start free. <em>Go premium when you're ready.</em></h2>
          <p className="p-lead">These platform plans run alongside (and independent of) the Cohort 1 fee above. Build your foundation on Basic. Upgrade to Premium for full operator access, unlimited credits and Cohort priority review.</p>
        </div>
        <div className="p-tier-grid">
          {/* BASIC */}
          <article className="p-tier" data-testid="tier-basic">
            <div className="p-tier-head">
              <div className="p-tier-name">Basic</div>
              <div className="p-tier-price-row">
                <span className="p-tier-currency" style={{ fontSize: 28 }}>AED</span>
                <span className="p-tier-num">0</span>
                <span className="p-tier-cycle">/ forever</span>
              </div>
              <div className="p-tier-byline">For exploring founders building their first venture</div>
            </div>
            <ul className="p-tier-list">
              <li><CheckCircle2 size={16} /> Apply to the Cohort 1 programme</li>
              <li><CheckCircle2 size={16} /> Public masterclass library access</li>
              <li><CheckCircle2 size={16} /> Founders &amp; Mentors directory (browse only)</li>
              <li><CheckCircle2 size={16} /> Job board: apply to listings</li>
              <li><CheckCircle2 size={16} /> Nova AI: 5 governance evaluations / month</li>
              <li><CheckCircle2 size={16} /> AI Masterclass syllabus generator (3 / month)</li>
              <li><CheckCircle2 size={16} /> Webinar Series: 1 free audience pass / month (the rest at AED 149)</li>
            </ul>
            <button
              className="p-tier-cta secondary"
              onClick={() => navigate('/auth?mode=signup')}
              data-testid="checkout-basic-btn"
            >
              Start free →
            </button>
            <div className="p-tier-fineprint">No card required · upgrade anytime</div>
          </article>

          {/* PREMIUM */}
          <article className="p-tier featured" data-testid="tier-premium">
            <div className="p-tier-ribbon">Most popular</div>
            <div className="p-tier-head">
              <div className="p-tier-name">Premium</div>
              <div className="p-tier-price-row">
                <span className="p-tier-currency" style={{ fontSize: 28 }}>AED</span>
                <span className="p-tier-num">1,099</span>
                <span className="p-tier-cycle">/ year</span>
              </div>
              <div className="p-tier-byline">For founders ready to scale with institutional governance</div>
            </div>
            <ul className="p-tier-list">
              <li><Sparkles size={16} className="prem" /> <strong>Cohort 1 priority application</strong> — fast-tracked review</li>
              <li><Sparkles size={16} className="prem" /> <strong>Free UAE incorporation consultancy</strong> <span style={{ opacity: 0.75, fontWeight: 400 }}>(advisory only · licence &amp; visa fees not included)</span></li>
              <li><Sparkles size={16} className="prem" /> <strong>1-on-1 mentor matching</strong> with Ministry-level operators</li>
              <li><CheckCircle2 size={16} /> Nova AI: <strong>unlimited</strong> governance evaluations + scoring</li>
              <li><CheckCircle2 size={16} /> AI Masterclass: <strong>unlimited</strong> syllabi + audio narration + curated lesson video library</li>
              <li><CheckCircle2 size={16} /> Live cohort masterclasses + recordings (lifetime access)</li>
              <li><CheckCircle2 size={16} /> Investor pipeline + warm intros to UAE Forum 2026 attendees</li>
              <li><CheckCircle2 size={16} /> Document automation: SAFEs, term sheets, board packs</li>
              <li><CheckCircle2 size={16} /> Cap table tooling + automated investor updates</li>
              <li><CheckCircle2 size={16} /> Webinar Series: <strong>all 12 monthly webinars complimentary</strong> (audience tier · last Saturday every month)</li>
              <li><CheckCircle2 size={16} /> Job board: post unlimited roles + verified company badge</li>
              <li><CheckCircle2 size={16} /> Private Telegram + alumni network (lifetime)</li>
            </ul>
            <button
              className="p-tier-cta primary"
              onClick={() => handleCheckout('premium')}
              disabled={loading}
              data-testid="checkout-premium-btn"
            >
              {loading ? (<><Loader2 className="animate-spin" size={16} /> Processing…</>) : 'Go Premium — AED 1,099 / yr'}
            </button>
            <div className="p-tier-fineprint">Annual billing · 7-day money-back guarantee</div>
          </article>
        </div>
      </section>

      {/* FAQ */}
      <section className="p-section" style={{ paddingTop: 0, paddingBottom: 100 }}>
        <div className="p-section-head">
          <div className="p-tag">FAQs</div>
          <h2 className="p-h2">Frequently <em>asked.</em></h2>
        </div>
        <div className="p-faq">
          {faqs.map((f, i) => (
            <div key={i} className="p-faq-item" data-testid={`faq-${i}`}>
              <div className="p-faq-q">{f.q}</div>
              <div className="p-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
